// ============================================
// FILE: utility/discountHelper.js
// Centralized discount calculation logic
// ============================================

/**
 * Calculate quantity-based bulk discount
 * @param {number} totalQty - Total quantity of items
 * @returns {number} Discount percentage (0-1)
 */
export const getQuantityDiscount = (totalQty) => {
  if (totalQty >= 100) return 0.20; // 20% OFF
  if (totalQty >= 50)  return 0.15; // 15% OFF
  if (totalQty >= 24)  return 0.10; // 10% OFF
  if (totalQty >= 10)  return 0.05; //  5% OFF
  return 0;
};

/**
 * Calculate low inventory discount.
 * Fires when the given inventory number is > 0 and <= 5.
 *
 * IMPORTANT: the "inventory number" passed in MUST be the item_code's
 * TOTAL combined stock across every variation/size/plating — never a
 * single variant's own stock. Low-stock status is a property of the
 * product (item_code) as a whole, not of any one size/color row.
 *
 * @param {number} inventory
 * @returns {number} Discount percentage (0-1)
 */
export const getLowInventoryDiscount = (inventory) => {
  return inventory > 0 && inventory <= 5 ? 0.10 : 0;
};

/**
 * Stack two discounts using compound calculation
 * @param {number} discount1 - First discount (0-1)
 * @param {number} discount2 - Second discount (0-1)
 * @returns {number} Combined discount (0-1)
 */
export const stackDiscounts = (discount1, discount2) => {
  return 1 - ((1 - discount1) * (1 - discount2));
};

/**
 * Get discount label for display
 * @param {number} quantityDiscount
 * @param {number} lowInventoryDiscount
 * @returns {string}
 */
export const getDiscountLabel = (quantityDiscount, lowInventoryDiscount) => {
  const labels = [];
  if (quantityDiscount === 0.20) labels.push("20% Bulk");
  else if (quantityDiscount === 0.15) labels.push("15% Bulk");
  else if (quantityDiscount === 0.10) labels.push("10% Bulk");
  else if (quantityDiscount === 0.05) labels.push("5% Bulk");
  if (lowInventoryDiscount > 0) labels.push("10%");
  return labels.join(" + ");
};

/**
 * Get short discount label (just percentages)
 * @param {number} totalDiscount - Total discount (0-1)
 * @returns {string}
 */
export const getShortDiscountLabel = (totalDiscount) => {
  if (totalDiscount <= 0) return "";
  return `${(totalDiscount * 100).toFixed(0)}% OFF`;
};

// ─────────────────────────────────────────────────────────────────────────────
// Real total inventory builder
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build a map of item_code → total real inventory from the product API response.
 *
 * This aggregated total (summed across every variation/size of the item_code)
 * is the ONLY correct source for the low-inventory discount, as well as for
 * the quantity-tier discount and informational display.
 *
 * @param {Array} products - Array from product_products GQL response
 *   Each product must have: item_code, product_variations[].product_variant_sizes[].quantity
 * @returns {Map<string, number>} item_code → total real inventory
 *
 * @example
 *   const stockMap = buildProductVariantsStockMap(productData.product_products);
 *   const itemCodeGroups = buildItemCodeGroups(cartItems, stockMap);
 */
export const buildProductVariantsStockMap = (products) => {
  const map = new Map();

  if (!Array.isArray(products)) return map;

  for (const product of products) {
    const itemCode = product.item_code;
    if (!itemCode) continue;

    let totalStock = 0;
    for (const variation of product.product_variations ?? []) {
      for (const variantSize of variation.product_variant_sizes ?? []) {
        totalStock += Number(variantSize.quantity) || 0;
      }
    }

    // Accumulate in case multiple products share an item_code
    map.set(itemCode, (map.get(itemCode) ?? 0) + totalStock);
  }

  return map;
};

// ─────────────────────────────────────────────────────────────────────────────
// Core group builder
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build item code groups from cart items.
 * Groups items by item_code and calculates discount totals.
 *
 * group.totalInventory is the item_code-level COMBINED stock across every
 * variant (preferring productVariantsStockMap, which reflects ALL variants
 * of the product — not just the ones currently in the cart).
 *
 * group.lowInventoryDiscount and group.totalDiscount are the correct,
 * item_code-aggregated values to use everywhere — cart page, checkout page,
 * and backend order total should all read from here (via
 * getItemDiscountComponents), NOT from any single variant's own stock.
 *
 * @param {Array}      cartItems
 * @param {Map|null}   productVariantsStockMap
 * @returns {Object} Groups keyed by item_code
 */
export const buildItemCodeGroups = (cartItems, productVariantsStockMap = null) => {
  const groups = {};

  cartItems.forEach(item => {
    const itemCode = item.item_code;
    if (!itemCode) return;

    if (!groups[itemCode]) {
      groups[itemCode] = {
        totalQuantity:  0,
        totalInventory: 0,
        items:          [],
      };
    }

    // ── Cart quantity: always sum from cart items ──────────────────────────
    if (item.wholesaleSelections && item.wholesaleSelections.length > 0) {
      item.wholesaleSelections.forEach(selection => {
        groups[itemCode].totalQuantity += selection.qty;
      });
    } else {
      groups[itemCode].totalQuantity += item.quantity || 0;
    }

    // ── Real inventory: prefer productVariantsStockMap (full product stock) ─
    if (productVariantsStockMap && productVariantsStockMap.has(itemCode)) {
      groups[itemCode].totalInventory = productVariantsStockMap.get(itemCode);
    } else {
      // ⚠️ FALLBACK (no map provided): sum cart items' own stock fields.
      // Less accurate than the map (only reflects variants currently in the
      // cart), but better than nothing if the stock query hasn't loaded yet.
      if (item.wholesaleSelections && item.wholesaleSelections.length > 0) {
        item.wholesaleSelections.forEach(selection => {
          const stock = selection.original_stock ?? selection.maxQty ?? 0;
          groups[itemCode].totalInventory += stock;
        });
      } else {
        const stock = item.original_stock ?? item.maxQty ?? 0;
        groups[itemCode].totalInventory += stock;
      }
    }

    groups[itemCode].items.push(item);
  });

  // Calculate discounts for each group
  Object.keys(groups).forEach(itemCode => {
    const group = groups[itemCode];
    group.quantityDiscount     = getQuantityDiscount(group.totalQuantity);
    group.lowInventoryDiscount = getLowInventoryDiscount(group.totalInventory);
    group.totalDiscount        = stackDiscounts(group.quantityDiscount, group.lowInventoryDiscount);
  });

  return groups;
};

// ─────────────────────────────────────────────────────────────────────────────
// Accessors
// ─────────────────────────────────────────────────────────────────────────────

export const getTotalItemDiscount = (item, itemCodeGroups) => {
  const itemCode = item.item_code;
  if (!itemCode || !itemCodeGroups?.[itemCode]) return 0;
  return itemCodeGroups[itemCode].totalDiscount || 0;
};

/**
 * Returns the item_code-aggregated discount components for a cart item.
 * Use this everywhere — cart page, checkout page, and the util that
 * calculates final order totals — so pricing can never drift between them.
 */
export const getItemDiscountComponents = (item, itemCodeGroups) => {
  const itemCode = item.item_code;
  if (!itemCode || !itemCodeGroups?.[itemCode]) {
    return { quantityDiscount: 0, lowInventoryDiscount: 0, totalDiscount: 0 };
  }
  const group = itemCodeGroups[itemCode];
  return {
    quantityDiscount:     group.quantityDiscount     || 0,
    lowInventoryDiscount: group.lowInventoryDiscount || 0,
    totalDiscount:        group.totalDiscount        || 0,
  };
};

export const getItemCodeTotalQty = (item, itemCodeGroups) => {
  const itemCode = item.item_code;
  if (!itemCode || !itemCodeGroups?.[itemCode]) return 0;
  return itemCodeGroups[itemCode].totalQuantity;
};

export const applyDiscount = (basePrice, discount) => {
  return basePrice * (1 - discount);
};

export const calculateSizePriceWithDiscount = (item, sizeSelection, selectedCurrency, totalDiscount) => {
  const weight              = Number(sizeSelection.weight) || 0;
  const pricePerGram        = Number(item.pricePerGram) || 0;
  const platingPricePerGram = Number(sizeSelection.plating_price_per_gram) || 0;
  const conversionRate      = selectedCurrency?.conversion_rate || 1;

  const isINRCurrency = selectedCurrency?.name?.toLowerCase() === 'inr';
  const basePrice = isINRCurrency
    ? (weight * (pricePerGram + platingPricePerGram))
    : (weight * (pricePerGram + platingPricePerGram)) / conversionRate;

  return applyDiscount(basePrice, totalDiscount);
};

export const formatDiscountPercentage = (discount, decimals = 0) => {
  if (discount <= 0) return "0%";
  return `${(discount * 100).toFixed(decimals)}%`;
};

export const logDiscountInfo = (context, item, discountInfo) => {
  console.log(`💰 Discount Info [${context}] for "${item.name}":`, {
    itemCode:             item.item_code,
    quantityDiscount:     formatDiscountPercentage(discountInfo.quantityDiscount),
    lowInventoryDiscount: formatDiscountPercentage(discountInfo.lowInventoryDiscount),
    totalDiscount:        formatDiscountPercentage(discountInfo.totalDiscount, 1),
    totalInventory:       discountInfo.totalInventory,
    totalQuantity:        discountInfo.totalQuantity,
  });
};

export default {
  getQuantityDiscount,
  getLowInventoryDiscount,
  stackDiscounts,
  getDiscountLabel,
  getShortDiscountLabel,
  buildProductVariantsStockMap,
  buildItemCodeGroups,
  getTotalItemDiscount,
  getItemDiscountComponents,
  getItemCodeTotalQty,
  applyDiscount,
  calculateSizePriceWithDiscount,
  formatDiscountPercentage,
  logDiscountInfo,
};